import { useState } from 'react'
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore } from './stores/authStore'
import { destinationForUserType } from './utils/userTypeDestination'
import { postRegisterTarget } from './utils/postRegisterTarget'

// Inauguration curtain overlay — shown once per browser (gated by the oath
// completion flag below) on first open, then the TB-elimination pledge overlay.
import InaugurationCurtain from './components/InaugurationCurtain'
import OathPledge, { OATH_TAKEN_KEY } from './components/OathPledge'

// Layouts
import AuthLayout from './components/layout/AuthLayout'

// Auth Pages
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import CompleteProfilePage from './pages/auth/CompleteProfilePage'

// Portal Pages (task-oriented public-health experience — primary)
import PortalHomePage from './pages/portal/HomePage'
import AboutTBPage from './pages/portal/AboutTBPage'
import IHaveTBPage from './pages/portal/IHaveTBPage'
import SupportAPatientPage from './pages/portal/SupportAPatientPage'
import BecomeASupporterPage from './pages/portal/BecomeASupporterPage'
import NgoHubPage from './pages/portal/NgoHubPage'
import VolunteerHubPage from './pages/portal/VolunteerHubPage'
import GetHelpPage from './pages/portal/GetHelpPage'
import FindTBCentrePage from './pages/portal/FindTBCentrePage'
import SymptomCheckerPage from './pages/portal/SymptomCheckerPage'
import RiskCheckerPage from './pages/portal/RiskCheckerPage'
import ResourcesPage from './pages/portal/ResourcesPage'
import VideosPage from './pages/portal/VideosPage'
import NewsPage from './pages/portal/NewsPage'
import TbAssistantPage from './pages/portal/TbAssistantPage'
import StoriesPage from './pages/portal/StoriesPage'
import ContactPage from './pages/portal/ContactPage'
import CommunityPage from './pages/portal/CommunityPage'
import { CommunityProvider } from './components/community/CommunityContext'
import PortalSearchPage from './pages/portal/SearchResultsPage'
import NotFoundPage from './pages/NotFoundPage'

// Main Pages
import HomePage from './pages/HomePage'
import LandingPage from './pages/landing/LandingPage'
import FeedPage from './pages/feed/FeedPage'
import PremiumFeedPage from './pages/feed/PremiumFeedPage'
import ProfilePage from './pages/profile/ProfilePage'
import ReelsPage from './pages/reels/ReelsPage'
import CreateReelPage from './pages/reels/CreateReelPage'
import ReelDetailPage from './pages/reel/ReelDetailPage'
import PostDetailPage from './pages/post/PostDetailPage'
import HashtagFeedPage from './pages/hashtag/HashtagFeedPage'
import MessagesPage from './pages/messages/MessagesPage'
import NotificationsPage from './pages/notifications/NotificationsPage'
import SettingsPage from './pages/settings/SettingsPage'
import KnowledgePage from './pages/knowledge/KnowledgePage'
import SearchPage from './pages/SearchPage'

// Social Pages
import GroupsPage from './pages/groups/GroupsPage'
import PagesPage from './pages/pages/PagesPage'
import SocialStoriesPage from './pages/stories/StoriesPage'
import SavedPostsPage from './pages/saved/SavedPostsPage'
import LiveStreamsPage from './pages/live/LiveStreamsPage'
import LiveStream from './pages/live/LiveStream'
import WatchLiveStream from './pages/live/WatchLiveStream'

// Gamification Pages
import AchievementsPage from './pages/gamification/AchievementsPage'
import ChallengesPage from './pages/gamification/ChallengesPage'

// Dashboard Pages - Role-Based
import CitizenDashboard from './pages/dashboard/CitizenDashboard'
import VolunteerDashboard from './pages/dashboard/VolunteerDashboard'
import NikshayMitraDashboard from './pages/dashboard/NikshayMitraDashboard'
import SupporterDashboard from './pages/dashboard/SupporterDashboard'

// Volunteer Community Dashboard Pages — dedicated shell for VOLUNTEER_COMMUNITY users
import CommunityDashboardHome from './pages/community/CommunityDashboardHome'
import CommunityFeedPage from './pages/community/CommunityFeedPage'
import CommunityCampaignsPage from './pages/community/CommunityCampaignsPage'
import CommunityEventsPage from './pages/community/CommunityEventsPage'
import CommunityMembersPage from './pages/community/CommunityMembersPage'
import CommunityLearningPage from './pages/community/CommunityLearningPage'
import CommunityResourcesPage from './pages/community/CommunityResourcesPage'
import CommunityActivityPage from './pages/community/CommunityActivityPage'
import CommunityRecognitionPage from './pages/community/CommunityRecognitionPage'
import CommunityAnnouncementsPage from './pages/community/CommunityAnnouncementsPage'
import CommunityProfilePage from './pages/community/CommunityProfilePage'
import CommunitySettingsPage from './pages/community/CommunitySettingsPage'
// Volunteer-person-centric dashboard pages (§3, §5, §8, §9 hub, §10 saved, §11 profile)
import CommunityOpportunitiesPage from './pages/community/CommunityOpportunitiesPage'
import CommunityMyActivitiesPage from './pages/community/CommunityMyActivitiesPage'
import CommunityCommunityPage from './pages/community/CommunityCommunityPage'
import CommunityCertificatesPage from './pages/community/CommunityCertificatesPage'
import CommunityMyProfilePage from './pages/community/CommunityMyProfilePage'
import CommunitySavedResourcesPage from './pages/community/CommunitySavedResourcesPage'

// Badges & Certificates — available to every logged-in user (admin-awarded
// badges auto-generate a certificate; see BadgeAdminController + CertificateService).
import MyBadgesPage from './pages/badges/MyBadgesPage'
import MyCertificatesPage from './pages/certificates/MyCertificatesPage'

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard'
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard'
import UserManagement from './pages/admin/UserManagement'
import ContentManagement from './pages/admin/ContentManagement'
import ReportsManagement from './pages/admin/ReportsManagement'
import AdminCompetitionsPage from './pages/admin/AdminCompetitionsPage'
import ContactInbox from './pages/admin/ContactInbox'
import SupportersReview from './pages/admin/SupportersReview'
import ResourcesManagement from './pages/admin/ResourcesManagement'
import VideosManagement from './pages/admin/VideosManagement'
import NewsManagement from './pages/admin/NewsManagement'
import AssistantManagement from './pages/admin/AssistantManagement'
import ActiveUsersPage from './pages/admin/ActiveUsersPage'
import GroupManagementPage from './pages/admin/GroupManagementPage'
import PostManagerPage from './pages/admin/PostManagerPage'
import ReelManagementPage from './pages/admin/ReelManagementPage'
import CertificatesManagement from './pages/admin/CertificatesManagement'
import BadgesManagement from './pages/admin/BadgesManagement'
import TestimonialsReview from './pages/admin/TestimonialsReview'
import FacilitiesManagement from './pages/admin/FacilitiesManagement'
import OathAnalyticsPage from './pages/admin/OathAnalyticsPage'

// Campaign Pages
import CampaignListPage from './pages/campaign/CampaignListPage'
import CampaignDetailPage from './pages/campaign/CampaignDetailPage'
import CreateCampaignPage from './pages/campaign/CreateCampaignPage'

// Competition Pages
import CompetitionListPage from './pages/competition/CompetitionListPage'
import CreateCompetitionPage from './pages/competition/CreateCompetitionPage'
import CompetitionDetailPage from './pages/competition/CompetitionDetailPage'
import CompetitionSubmitPage from './pages/competition/CompetitionSubmitPage'
import MySubmissionsPage from './pages/competition/MySubmissionsPage'
import CompetitionAnalyticsPage from './pages/admin/CompetitionAnalyticsPage'
import AdminSubmissionsPage from './pages/admin/AdminSubmissionsPage'

// Protected Route Component - Checks authentication only.
// Profile/role selection is NOT forced here: a returning user without a
// userType is allowed to browse protected pages (landing on /feed by default).
// The role-selection ("complete profile") flow only runs after registration,
// via the standalone /complete-profile route.
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, hasHydrated } = useAuthStore()
  const location = useLocation()

  // Wait for zustand `persist` to rehydrate from localStorage before deciding.
  // On a full page load the store starts unauthenticated; without this gate a
  // logged-in user opening a shared /post/:id or /reel/:id is wrongly sent to
  // /login before their persisted session is restored.
  if (!hasHydrated) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // Preserve the destination the user was trying to reach so LoginPage can
    // return them there after a successful login (e.g. a shared /post/:id or
    // /reel/:id deep link). Without this state, a deep-linked visitor lands on
    // their default dashboard instead of the post/reel they opened.
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  return <>{children}</>
}

// Admin Route Component - Only for ADMIN and SUPER_ADMIN
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, user, hasHydrated } = useAuthStore()

  // Same hydration gate as ProtectedRoute — don't redirect before the persisted
  // session is restored.
  if (!hasHydrated) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (user?.role !== 'ADMIN' && user?.role !== 'SUPER_ADMIN') {
    return <Navigate to="/feed" replace />
  }

  return <>{children}</>
}

// When an ALREADY-authenticated user lands on /login, send them onward. This
// handles two cases:
//   1. A logged-in user manually opens /login → bounce to their default home.
//   2. ⭐ A logged-OUT user was sent to /login by `ProtectedRoute` (via
//      `state={{ from: location }}`) from a shared /post/:id or /reel/:id deep
//      link, then logs in. The instant `isAuthenticated` flips true, the /login
//      route re-renders with this component — so it MUST honor the saved `from`
//      destination, otherwise the user is sent to their default dashboard and
//      never returns to the post/reel they clicked. (LoginPage's own return-to
//      effect does NOT get to run here, because this route swaps LoginPage out
//      for this redirect in the same commit that auth becomes true — so the
//      route-level redirect is the one that actually fires. Honoring `from`
//      here is the fix.) With no `from` state (a normal direct login), the
//      existing default-destination logic runs unchanged.
const LoginAuthRedirect = () => {
  const { user } = useAuthStore()
  const location = useLocation()
  const from = (location.state as any)?.from
  const returnTo = from?.pathname && from.pathname !== '/login'
    ? from.pathname + (from.search || '')
    : null
  if (returnTo) return <Navigate to={returnTo} replace />
  const dest = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN'
    ? '/admin/dashboard'
    : destinationForUserType(user?.userType)
  return <Navigate to={dest} replace />
}

// The inauguration curtain + TB-elimination pledge replay until the visitor
// has taken (or been verified as having taken) the pledge. The gate lives in
// localStorage so it survives across sessions — once `tbmukt_oath_taken` is set
// (by OathPledge on a successful submit or verify), neither the curtain nor
// the pledge overlay shows again on this browser. Fresh visitors (no flag) get
// the curtain → pledge flow; the pledge is mandatory (no skip) per product.
//
// ⚇ TEMPORARY: the inauguration curtain animation is hidden for the upcoming
// deploy. Set CURTAIN_ENABLED back to `true` to restore the curtain → pledge
// flow. While disabled, the curtain is suppressed but the pledge overlay
// (OathPledge) still shows for fresh visitors — only the curtain animation is
// skipped, so the pledge gate + analytics keep working unchanged.
const CURTAIN_ENABLED = false

function App() {
  const { isAuthenticated, user, pendingRole } = useAuthStore()

  // Show the curtain only if it is enabled AND the pledge has not yet been
  // taken/verified on this browser. The initializer is read-only so React
  // StrictMode's dev double-invoke stays consistent; the side effect (writing
  // the flag) happens inside OathPledge on a successful submit/verify.
  const [showCurtain, setShowCurtain] = useState<boolean>(() => {
    if (!CURTAIN_ENABLED) return false
    try {
      return localStorage.getItem(OATH_TAKEN_KEY) == null
    } catch {
      return false
    }
  })
  // When the curtain is disabled, reveal the pledge overlay directly for
  // fresh visitors (no pledge taken yet). When the curtain is enabled, the
  // pledge is revealed by handleCurtainDone after the animation finishes.
  const [showOath, setShowOath] = useState<boolean>(() => {
    if (!CURTAIN_ENABLED) {
      try {
        return localStorage.getItem(OATH_TAKEN_KEY) == null
      } catch {
        return false
      }
    }
    return false
  })

  // Curtain finished opening → reveal the pledge overlay. The pledge overlay
  // writes the localStorage gate itself (on accept/verify) and then calls
  // onAccept to dismiss itself.
  const handleCurtainDone = () => {
    setShowCurtain(false)
    setShowOath(true)
  }
  const handleOathAccepted = () => {
    setShowOath(false)
  }

  return (
    <>
      {showCurtain && (
        <InaugurationCurtain onDone={handleCurtainDone} />
      )}
      {showOath && (
        <OathPledge onAccept={handleOathAccepted} />
      )}
      <Routes>
      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <LoginAuthRedirect />} />
        <Route path="/register" element={
          !isAuthenticated ? (
            <RegisterPage />
          ) : (
            // Authenticated visitor on /register: route to the post-register
            // destination derived from the role chosen at registration
            // (pendingRole) — NOT a blanket /feed, which would drop the role
            // and strand the user on the "select your role" screen.
            <Navigate to={!user?.userType ? postRegisterTarget(pendingRole) : destinationForUserType(user?.userType)} replace />
          )
        } />
        <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPasswordPage /> : <Navigate to="/feed" />} />
      </Route>

      {/* Complete Profile Route - For users who haven't selected userType.
          Once a userType IS set (e.g. auto-applied from the registration role),
          redirect to that role's dashboard — not a generic /feed — so the
          programmatic navigate from CompleteProfilePage and this guard agree. */}
      <Route path="/complete-profile" element={
        isAuthenticated ? (
          !user?.userType && user?.role !== 'ADMIN' && user?.role !== 'SUPER_ADMIN'
            ? <CompleteProfilePage />
            : <Navigate to={destinationForUserType(user?.userType)} replace />
        ) : <Navigate to="/login" replace />
      } />

      {/* ===================== Public Portal (task-oriented) — primary experience ===================== */}
      <Route path="/" element={<PortalHomePage />} />
      <Route path="/about-tb" element={<AboutTBPage />} />
      <Route path="/i-have-tb" element={<IHaveTBPage />} />
      <Route path="/support-a-patient" element={<SupportAPatientPage />} />
      <Route path="/become-a-supporter" element={<BecomeASupporterPage />} />
      <Route path="/ngo" element={<NgoHubPage />} />
      <Route path="/volunteer" element={<VolunteerHubPage />} />
      <Route path="/get-help" element={<GetHelpPage />} />
      <Route path="/find-tb-centre" element={<FindTBCentrePage />} />
      <Route path="/symptom-checker" element={<SymptomCheckerPage />} />
      <Route path="/am-i-at-risk" element={<RiskCheckerPage />} />
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/videos" element={<VideosPage />} />
      <Route path="/news" element={<NewsPage />} />
      <Route path="/assistant" element={<TbAssistantPage />} />
      <Route path="/stories" element={<StoriesPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/community" element={<CommunityPage />} />

      {/* Legacy marketing landing page (kept for reference; portal home is now "/") */}
      <Route path="/landing" element={<LandingPage />} />

      {/* Premium Feed & Profile (each page wraps itself in GovLayout) */}
      <Route path="/feed" element={<ProtectedRoute><PremiumFeedPage /></ProtectedRoute>} />
      <Route path="/feed/classic" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
      <Route path="/hashtag/:tag" element={<ProtectedRoute><HashtagFeedPage /></ProtectedRoute>} />
      <Route path="/profile/:username?" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      {/* Single post / reel detail — opened from the profile grid tiles.
          Renders the item exactly like the feed/reels with all actions. */}
      <Route path="/post/:id" element={<ProtectedRoute><PostDetailPage /></ProtectedRoute>} />
      <Route path="/reel/:id" element={<ProtectedRoute><ReelDetailPage /></ProtectedRoute>} />

      {/* Pages that wrap themselves in GovLayout (left nav sidebar + header) */}
      <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      <Route path="/reels" element={<ReelsPage />} />
      <Route path="/create-reel" element={<ProtectedRoute><CreateReelPage /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/knowledge" element={<KnowledgePage />} />
      {/* Unified public-content search (knowledge, campaigns, resources,
          testimonials, facilities). The header search box navigates here. */}
      <Route path="/search" element={<PortalSearchPage />} />
      {/* Social search (users & groups) — community feature, preserved. */}
      <Route path="/community/search" element={<SearchPage />} />

      {/* Social Routes - GovLayout provides sidebar */}
      <Route path="/groups" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
      <Route path="/pages" element={<PagesPage />} />
      <Route path="/community/stories" element={<ProtectedRoute><SocialStoriesPage /></ProtectedRoute>} />
      <Route path="/saved" element={<ProtectedRoute><SavedPostsPage /></ProtectedRoute>} />
      <Route path="/live" element={<LiveStreamsPage />} />
      <Route path="/live/start" element={<ProtectedRoute><LiveStream /></ProtectedRoute>} />
      <Route path="/live/:streamId" element={<WatchLiveStream />} />

      {/* Campaign Routes */}
      <Route path="/campaigns" element={<CampaignListPage />} />
      <Route path="/campaigns/create" element={<ProtectedRoute><CreateCampaignPage /></ProtectedRoute>} />
      <Route path="/campaigns/:id" element={<CampaignDetailPage />} />

      {/* Competition Routes */}
      <Route path="/competitions" element={<CompetitionListPage />} />
      <Route path="/competitions/create" element={<ProtectedRoute><CreateCompetitionPage /></ProtectedRoute>} />
      <Route path="/competitions/:id" element={<CompetitionDetailPage />} />
      <Route path="/competitions/:id/submit" element={<ProtectedRoute><CompetitionSubmitPage /></ProtectedRoute>} />
      <Route path="/competitions/my-submissions" element={<ProtectedRoute><MySubmissionsPage /></ProtectedRoute>} />

      {/* Gamification Routes */}
      <Route path="/achievements" element={<ProtectedRoute><AchievementsPage /></ProtectedRoute>} />
      <Route path="/challenges" element={<ProtectedRoute><ChallengesPage /></ProtectedRoute>} />

      {/* Badges & Certificates — every logged-in user (admin-awarded badges
          auto-generate a downloadable certificate). Each page self-wraps in
          GovLayout; the APIs are user-id-keyed, not community-scoped. */}
      <Route path="/badges" element={<ProtectedRoute><MyBadgesPage /></ProtectedRoute>} />
      <Route path="/certificates" element={<ProtectedRoute><MyCertificatesPage /></ProtectedRoute>} />

      {/* Role-Based Dashboard Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><CitizenDashboard /></ProtectedRoute>} />
      <Route path="/citizen-dashboard" element={<ProtectedRoute><CitizenDashboard /></ProtectedRoute>} />
      <Route path="/volunteer-dashboard" element={<ProtectedRoute><VolunteerDashboard /></ProtectedRoute>} />
      {/* Doctor / Health Worker dashboards were retired — old links redirect to the feed. */}
      <Route path="/doctor-dashboard" element={<Navigate to="/feed" replace />} />
      <Route path="/nikshay-mitra-dashboard" element={<ProtectedRoute><NikshayMitraDashboard /></ProtectedRoute>} />
      <Route path="/health-worker-dashboard" element={<Navigate to="/feed" replace />} />
      {/* TB-supporter categories — one adaptive dashboard, adapts per :type slug */}
      <Route path="/supporter-dashboard/:type" element={<ProtectedRoute><SupporterDashboard /></ProtectedRoute>} />

      {/* Volunteer Community Dashboard — dedicated shell for VOLUNTEER_COMMUNITY users.
          A single CommunityProvider wraps every community route (via Outlet) so the
          pages can call useCommunity() in their own bodies, above where CommunityLayout
          renders. The provider fetches /community/my once per shell mount. */}
      <Route path="/community-dashboard" element={<ProtectedRoute><CommunityProvider><Outlet /></CommunityProvider></ProtectedRoute>}>
        <Route index element={<CommunityDashboardHome />} />
        <Route path="opportunities" element={<CommunityOpportunitiesPage />} />
        <Route path="my-activities" element={<CommunityMyActivitiesPage />} />
        <Route path="community" element={<CommunityCommunityPage />} />
        <Route path="certificates" element={<CommunityCertificatesPage />} />
        <Route path="my-profile" element={<CommunityMyProfilePage />} />
        <Route path="saved-resources" element={<CommunitySavedResourcesPage />} />
        <Route path="feed" element={<CommunityFeedPage />} />
        <Route path="campaigns" element={<CommunityCampaignsPage />} />
        {/* /events now redirects to the unified Volunteer Opportunities page.
            Absolute target — a relative "opportunities" would append to
            /community-dashboard/events and 404. */}
        <Route path="events" element={<Navigate to="/community-dashboard/opportunities" replace />} />
        <Route path="members" element={<CommunityMembersPage />} />
        <Route path="learning" element={<CommunityLearningPage />} />
        <Route path="resources" element={<CommunityResourcesPage />} />
        <Route path="activities" element={<CommunityActivityPage />} />
        <Route path="recognition" element={<CommunityRecognitionPage />} />
        {/* /badges alias for the Recognition page (spec §7 name) */}
        <Route path="badges" element={<CommunityRecognitionPage />} />
        <Route path="announcements" element={<CommunityAnnouncementsPage />} />
        <Route path="profile" element={<CommunityProfilePage />} />
        <Route path="settings" element={<CommunitySettingsPage />} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin/dashboard" element={
        <AdminRoute>
          {user?.role === 'SUPER_ADMIN' ? <SuperAdminDashboard /> : <AdminDashboard />}
        </AdminRoute>
      } />
      <Route path="/admin/users" element={<AdminRoute><UserManagement /></AdminRoute>} />
      <Route path="/admin/active-users" element={<AdminRoute><ActiveUsersPage /></AdminRoute>} />
      <Route path="/admin/groups-admin" element={<AdminRoute><GroupManagementPage /></AdminRoute>} />
      <Route path="/admin/posts" element={<AdminRoute><PostManagerPage /></AdminRoute>} />
      <Route path="/admin/reels-admin" element={<AdminRoute><ReelManagementPage /></AdminRoute>} />
      <Route path="/admin/content" element={<AdminRoute><ContentManagement /></AdminRoute>} />
      <Route path="/admin/reports" element={<AdminRoute><ReportsManagement /></AdminRoute>} />
      <Route path="/admin/contact" element={<AdminRoute><ContactInbox /></AdminRoute>} />
      <Route path="/admin/supporters" element={<AdminRoute><SupportersReview /></AdminRoute>} />
      <Route path="/admin/resources" element={<AdminRoute><ResourcesManagement /></AdminRoute>} />
      <Route path="/admin/videos" element={<AdminRoute><VideosManagement /></AdminRoute>} />
      <Route path="/admin/news" element={<AdminRoute><NewsManagement /></AdminRoute>} />
      <Route path="/admin/assistant" element={<AdminRoute><AssistantManagement /></AdminRoute>} />
      <Route path="/admin/certificates" element={<AdminRoute><CertificatesManagement /></AdminRoute>} />
      <Route path="/admin/badges" element={<AdminRoute><BadgesManagement /></AdminRoute>} />
      <Route path="/admin/testimonials" element={<AdminRoute><TestimonialsReview /></AdminRoute>} />
      <Route path="/admin/facilities" element={<AdminRoute><FacilitiesManagement /></AdminRoute>} />
      <Route path="/admin/oaths" element={<AdminRoute><OathAnalyticsPage /></AdminRoute>} />
      <Route path="/admin/competitions" element={<AdminRoute><AdminCompetitionsPage /></AdminRoute>} />
      <Route path="/admin/competitions/analytics" element={<AdminRoute><CompetitionAnalyticsPage /></AdminRoute>} />
      <Route path="/admin/competitions/:id/analytics" element={<AdminRoute><CompetitionAnalyticsPage /></AdminRoute>} />
      <Route path="/admin/competitions/:competitionId/submissions" element={<AdminRoute><AdminSubmissionsPage /></AdminRoute>} />

      {/* Catch all — 404 (no more silent redirect to home) */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </>
  )
}

export default App